import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mountain, Gift } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    const code = localStorage.getItem("ascend_ref");
    if (!code) return;
    api.get(`/affiliate/resolve/${code}`)
      .then((r) => setReferrer(r.data))
      .catch(() => localStorage.removeItem("ascend_ref"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(username, email, password);
      toast.success("Welcome to Ascend. Time to scan.");
      nav("/scan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative z-10 bg-[var(--bg)] text-[var(--text)]">
      <div className="hidden lg:block lg:w-1/2 relative border-r border-[var(--border)] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/11338008/pexels-photo-11338008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
          alt="Ascend"
          className="absolute inset-0 w-full h-full object-cover grayscale opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-black/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <span className="tape-label">NEW SUBJECT</span>
          <h2 className="font-display text-6xl mt-4 leading-none">
            ENROLL<br />IN THE<br />ASCENT.
          </h2>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="p-6 border-b border-[var(--border)] flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="register-brand">
            <Mountain className="w-5 h-5 text-[var(--primary)]" />
            <span className="font-display text-2xl tracking-wider">ASCEND</span>
          </Link>
          <Link to="/login" className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white" data-testid="register-to-login">
            Have an account? Sign in →
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <form onSubmit={submit} className="w-full max-w-md">
            <span className="tape-label">CREATE ACCOUNT</span>
            <h1 className="font-display text-5xl mt-4 mb-8 leading-none">BEGIN<br />ASCENDING.</h1>

            {referrer && (
              <div className="mb-6 border border-[var(--primary)] bg-[var(--primary)]/5 px-4 py-3 flex items-center gap-3" data-testid="register-referral-banner">
                <Gift className="w-4 h-4 text-[var(--primary)] shrink-0" />
                <div className="text-xs font-mono">
                  <span className="text-[var(--text-muted)] uppercase tracking-widest">Invited by </span>
                  <span className="text-white">@{referrer.username}</span>
                  <span className="text-[var(--text-muted)]"> · code {referrer.code}</span>
                </div>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Username</label>
                <input
                  type="text"
                  required
                  minLength={3}
                  maxLength={20}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="brutal"
                  data-testid="register-username-input"
                  placeholder="sigma_007"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brutal"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="brutal"
                  data-testid="register-password-input"
                  placeholder="At least 6 characters"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-brutal w-full"
                data-testid="register-submit-button"
              >
                {loading ? "Enrolling..." : "Create Account →"}
              </button>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center">
                Free forever. No card required.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
